import { useCallback, useContext, useMemo } from "react";

//Components
import {
  FormControl,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

//Contexts
import { UserContext } from "../../Contexts/UserContext";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { ACTIVE_INCIDENTS_ACTIONS } from "../../Actions/ActiveIncidentsActions";

//Fetching
import { useMutation, useApolloClient } from "@apollo/client";
import { SET_OFFICER_INCIDENT_TIMES } from "../../Mutations/SetOfficerIncidentTimes";
import { SET_SECONDARY_LOCATION } from "../../Mutations/SetSecondaryLocation";
import { REMOVE_OFFICER_FROM_INCIDENT } from "../../Mutations/RemoveOfficerFromIncident";

//Styling
import { useStyles } from "../../Styles/styles";
import deleteButton from "../../Assets/deleteButton.png";

//Utils
import {
  formatOfficerOriBadge,
  parseDateFlexible,
  updateOfficerTimesInOis,
} from "../../utils/utils";
import { MutationContext } from "../../Contexts/MutationContext";
import { DashboardContext } from "../../Contexts/DashboardContext";
import { SetupContext } from "../../Contexts/SetupContext";
import { shouldBeLocked } from "../../utils/Roles";
import { officerHasInUseScales } from "./officerHasInUseScales";
import ResponseTimes from "./ResponseTimes";

import moment from "moment";

const RespondingOfficer = ({
  numOfOfficers,
  oisIndex,
  ois,
  oiss,
  incidentIndex,
  incidentId,
  incidentReviewId,
  incidentDetailId,
  addEmptyOis,
  disabled,
  userRoles,
  isOnIncident,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const client = useApolloClient();
  const classes = useStyles();
  const { user, allOfficers, responseErrorHandler } = useContext(UserContext);
  const { incidents, incidentDispatch } = useContext(IncidentsContext);
  const {
    addOrUpdateOfficerInIncident,
    setStatus,
    updateIncidentDetail,
    updateIncidentReview,
    updateCallTeam,
  } = useContext(MutationContext);
  const { activeIncidentDispatch, setAvailOfficer, availableOfficerDispatch } =
    useContext(DashboardContext);
  const { setup } = useContext(SetupContext);
  const { REMOVE_OIS_FROM_ACTIVE_INCIDENT_TABLE } = ACTIVE_INCIDENTS_ACTIONS;

  const [setOfficerIncidentTime] = useMutation(SET_OFFICER_INCIDENT_TIMES, {
    context: {
      serializationKey: "incidentQueue",
    },
    onError: error => console.error(JSON.stringify(error)),
    onCompleted: data => data && responseErrorHandler(data),
  });
  const [setSecondaryLocation] = useMutation(SET_SECONDARY_LOCATION, {
    onCompleted: data => data && responseErrorHandler(data),
  });
  const [removeOfficerFromIncident] = useMutation(
    REMOVE_OFFICER_FROM_INCIDENT,
    { onCompleted: data => data && responseErrorHandler(data) },
  );

  const {
    UPDATE_INCIDENT,
    UPDATE_OIS,
    REMOVE_OIS,
    UPDATE_INCIDENT_DETAIL,
    UPDATE_INCIDENT_REVIEW,
  } = INCIDENT_ACTIONS;

  const handlePrimaryOfficerChange = e => {
    const bool = e.target.checked;
    const name = e.target.value;
    const incident = incidents[incidentIndex];
    if (bool) {
      incident?.officerIncidentSpecifics.forEach(o => {
        let value = o?.id === ois?.id || o.id === ois?.uuid ? true : false;
        if (
          value &&
          !incident?.team?.id &&
          o?.officer?.deptAttribute?.team?.id
        ) {
          updateIncidentTeam(o?.officer?.deptAttribute?.team);
        }
        incidentDispatch({
          type: UPDATE_OIS,
          payload: {
            incidentId: ois.incidentId,
            oisId: o.id,
            uuid: o?.uuid,
            property: name,
            value: value,
          },
        });
        setOfficerIncidentTime({
          variables: {
            input: {
              originatorId: user?.id,
              officerIncidentSpecificId: o?.id ? o?.id : o?.uuid,
              [name]: value,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      });
      incidentDispatch({
        type: UPDATE_INCIDENT_DETAIL,
        payload: {
          incidentDetailId: incidentDetailId,
          incidentIndex: incidentIndex,
          property: "investigatingOfficer",
          value: ois?.officer,
        },
      });
      incidentDispatch({
        type: UPDATE_INCIDENT_REVIEW,
        payload: {
          incidentIndex: incidentIndex,
          property: "investigatingOfficer",
          value: ois?.officer,
        },
      });
      updateIncidentReview({
        variables: {
          input: {
            incidentReviewId: incidentReviewId,
            originatorId: user?.id,
            incidentId: incidentId,
            investigatingOfficerId: ois?.officer?.id,
            reviewStatusId: setup.setupLists.reviewStatuses.find(
              r => r?.name === "Pending",
            )?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      updateIncidentDetail({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId,
            incidentDetailId: incidentDetailId,
            investigatingOfficerId: ois?.officer?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
  };

  const updateIncidentTeam = team => {
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        id: incidentId,
        property: "team",
        value: team,
      },
    });
    updateCallTeam({
      variables: {
        input: {
          incidentId: incidentId,
          originatorId: user?.id,
          teamId: team?.id,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const handleOfficerChange = (officer) => {
    const momentNow = moment.utc();
    updateOfficerTimesInOis(
      ois,
      "dispatchedAt",
      momentNow,
      officer,
      setup?.setupLists?.logCodes,
      incidentDispatch,
      activeIncidentDispatch,
      incidentId,
      setStatus,
      user?.id,
      setOfficerIncidentTime,
      addOrUpdateOfficerInIncident,
    );

    if (ois?.primaryOfficer) {
      if (
        !incidents[incidentIndex]?.team?.id &&
        officer?.deptAttribute?.team?.id
      ) {
        updateIncidentTeam(officer?.deptAttribute?.team);
      }

      incidentDispatch({
        type: UPDATE_INCIDENT_DETAIL,
        payload: {
          incidentDetailId: incidentDetailId,
          incidentIndex: incidentIndex,
          property: "investigatingOfficer",
          value: officer,
        },
      });
      incidentDispatch({
        type: UPDATE_INCIDENT_REVIEW,
        payload: {
          incidentIndex: incidentIndex,
          property: "investigatingOfficer",
          value: officer,
        },
      });
      updateIncidentReview({
        variables: {
          input: {
            incidentReviewId: incidentReviewId,
            originatorId: user?.id,
            incidentId: incidentId,
            investigatingOfficerId: officer?.id,
            reviewStatusId: setup.setupLists.reviewStatuses.find(
              r => r?.name === "Pending",
            )?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      updateIncidentDetail({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId,
            incidentDetailId: incidentDetailId,
            investigatingOfficerId: officer?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
  };

  const handleDateChange = useCallback(
    (_value, name, date) => {
      const parsedDate = parseDateFlexible(date);
      updateOfficerTimesInOis(
        ois,
        name,
        parsedDate,
        ois?.officer,
        setup?.setupLists?.logCodes,
        incidentDispatch,
        activeIncidentDispatch,
        incidentId,
        setStatus,
        user?.id,
        setOfficerIncidentTime,
        null,
        setAvailOfficer,
        availableOfficerDispatch,
      );
    },
    [
      ois,
      setup?.setupLists?.logCodes,
      incidentDispatch,
      activeIncidentDispatch,
      incidentId,
      setStatus,
      user?.id,
      setOfficerIncidentTime,
      setAvailOfficer,
      availableOfficerDispatch,
    ],
  );

  const handleSecondaryLocationUpdate = e => {
    const name = e.target.name;
    const location = e.target.value;
    let input = {
      originatorId: user?.id,
      officerIncidentSpecificId: ois.id ? ois.id : ois.uuid,
      secondaryAddress: location,
    };
    incidentDispatch({
      type: UPDATE_OIS,
      payload: {
        incidentId: ois.incidentId,
        oisId: ois.id,
        incidentIndex: incidentIndex,
        oisIndex: oisIndex,
        uuid: ois.uuid,
        property: name,
        value: location,
      },
    });
    setSecondaryLocation({
      variables: { input },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  //add active incident dispatch here
  const removeOfficerHandler = async (index, ois) => {
    if (window.confirm("Are you sure you want to remove this officer?")) {
      //TODO: add check of the s&w scales
      const { hasAssociation, associationErrors } = await officerHasInUseScales(
        ois?.officer,
        incidents[incidentIndex],
        user,
        client,
      );
      if (hasAssociation) {
        alert(associationErrors.join("\n"));
        return;
      }
      incidentDispatch({
        type: REMOVE_OIS,
        payload: { incidentId: ois.incidentId, oisId: ois.id, uuid: ois.uuid },
      });
      activeIncidentDispatch({
        type: REMOVE_OIS_FROM_ACTIVE_INCIDENT_TABLE,
        payload: { incidentId: incidentId, oisId: ois?.id },
      });
      if (ois.id) {
        removeOfficerFromIncident({
          variables: {
            input: {
              officerIncidentSpecificId: ois.id ? ois.id : ois.uuid,
              originatorId: user?.id,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      }
      if (numOfOfficers === 1) {
        //throttled the mutation in this function
        addEmptyOis();
      }
    } else {
      return;
    }
  };

  const timeArr = useMemo(() => {
    return [
      {
        disabled: memoizedLocked,
        timeString: ois?.dispatchedAt,
        handleChange: handleDateChange,
        timeName: "dispatchedAt",
        label: "Dispatched",
      },
      {
        disabled: memoizedLocked,
        timeString: ois?.enrouteAt,
        handleChange: handleDateChange,
        timeName: "enrouteAt",
        label: "Enroute",
      },
      {
        disabled: memoizedLocked,
        timeString: ois?.onSceneAt,
        handleChange: handleDateChange,
        timeName: "onSceneAt",
        label: "On Scene",
      },
      {
        disabled: memoizedLocked || !isOnIncident,
        timeString: ois?.clearedAt,
        handleChange: handleDateChange,
        timeName: "clearedAt",
        label: "Cleared",
      },
    ];
  }, [
    memoizedLocked,
    ois?.dispatchedAt,
    ois?.enrouteAt,
    ois?.onSceneAt,
    ois?.clearedAt,
    handleDateChange,
    isOnIncident,
  ]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        width: "100%",
        border: "1px solid grey",
        borderRadius: "5px",
        marginBottom: "4vh",
        justifyContent: "space-between",
      }}
      autoComplete="off"
      onSubmit={() => console.log("Handle submit")}
    >
      <Autocomplete
        // autoSelect
        disableClearable
        disabled={memoizedLocked}
        multiple={false}
        value={
          incidents[incidentIndex]?.dispatchableOfficers
            ? incidents[incidentIndex]?.dispatchableOfficers?.find(
                officer => officer?.id === ois?.officer?.id,
              ) || ois?.officer
            : allOfficers?.find(
                officer =>
                  officer?.id?.toString() === ois?.officer?.id?.toString(),
              ) || ois?.officer
        }
        id={"responding-officer-" + ois?.officer?.id}
        options={incidents[incidentIndex]?.dispatchableOfficers || allOfficers}
        getOptionDisabled={option =>
          oiss.some(
            o =>
              !o?.clearedAt &&
              o?.officer?.id?.toString() === option?.id?.toString(),
          )
        }
        getOptionLabel={option => formatOfficerOriBadge(option)}
        style={{ width: "125px" }}
        renderInput={params => (
          <TextField
            {...params}
            InputLabelProps={{ shrink: true }}
            InputProps={{ ...params.InputProps }}
            label="Responding Officer"
          />
        )}
        onChange={(e, value) => handleOfficerChange(value, "officer")}
        autoHighlight
        className={classes.smallMatchMargin}
      />
      <FormControl variant="outlined" className={classes.formControl}>
        <FormControlLabel
          control={
            <Checkbox
              inputProps={{
                "data-cy": "responding-officer-primary-checkbox-" + ois?.id,
              }}
              checked={ois?.primaryOfficer || false}
              onChange={handlePrimaryOfficerChange}
              value="primaryOfficer"
              color="primary"
              disabled={memoizedLocked}
            />
          }
          label="Primary"
        />
      </FormControl>
      <ResponseTimes timeArr={timeArr} />
      <TextField
        InputLabelProps={{ shrink: true }}
        name="secondaryAddress"
        label="Secondary Location"
        disabled={memoizedLocked}
        className={classes.smallMatchMargin}
        defaultValue={ois?.secondaryAddress}
        onKeyDown={e => {
          if (e.key === "Enter") {
            // handleSecondaryLocationUpdate(e)
            document.querySelectorAll(":focus").forEach(el => el.blur());
          }
        }}
        onBlur={e => handleSecondaryLocationUpdate(e)}
        InputProps={{ className: classes.input }}
      />
      <FormControl
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        className={classes.formControl}
      >
        <button
          type="button"
          onClick={
            !memoizedLocked ? () => removeOfficerHandler(oisIndex, ois) : null
          }
          disabled={memoizedLocked}
          style={{ background: "none", border: 0, padding: 0 }}
        >
          <img
            data-cy={"delete-officer-button-" + ois?.id}
            src={deleteButton}
            className={classes.pngButton}
            alt="Delete Officer"
          />
        </button>
      </FormControl>
    </div>
  );
};

export default RespondingOfficer;
